/**
 * Legacy Compatibility Layer
 * 
 * Maps between new normalized location structure and legacy field names.
 * Use this to support older components that expect legacy fields like:
 * - from / fromAirport / airport_id
 * - to / toAddress
 * - pickup_coords / dropoff_coords
 * 
 * This allows gradual migration without breaking existing functionality.
 */

import { createBookingLocation, LOCATION_SOURCES } from './bookingLocation';

/**
 * Legacy field mappings
 */
const LEGACY_PICKUP_FIELDS = {
  // Old field -> New field
  'from': 'pickup_location',
  'fromAirport': 'pickup_location',
  'from_airport': 'pickup_location',
  'airport_id': 'pickup_airport_code',
  'airportId': 'pickup_airport_code',
  'pickup_coords': null, // Special handling
  'pickupCoords': null,  // Special handling
};

const LEGACY_DROPOFF_FIELDS = {
  'to': 'dropoff_location',
  'toAddress': 'dropoff_location',
  'to_address': 'dropoff_location',
  'destination': 'dropoff_location',
  'dropoff_coords': null, // Special handling
  'dropoffCoords': null,  // Special handling
};

/**
 * Convert booking with legacy fields to normalized structure
 * @param {Object} legacyBooking - Booking data with potentially legacy fields
 * @returns {Object} Booking with normalized fields
 */
export const fromLegacy = (legacyBooking) => {
  if (!legacyBooking) return null;
  
  const normalized = { ...legacyBooking };
  
  // Normalize pickup location
  normalized.pickup_location = 
    legacyBooking.pickup_location ||
    legacyBooking.from ||
    legacyBooking.fromAirport ||
    legacyBooking.from_airport ||
    '';
  
  // Normalize pickup type (infer if not present)
  if (!normalized.pickup_type) {
    if (legacyBooking.airport_id || legacyBooking.airportId || legacyBooking.pickup_airport_code) {
      normalized.pickup_type = 'airport';
    } else if (legacyBooking.pickup_place_id) {
      normalized.pickup_type = 'custom';
    } else {
      normalized.pickup_type = 'manual';
    }
  }
  
  // Normalize airport code
  normalized.pickup_airport_code = 
    legacyBooking.pickup_airport_code ||
    legacyBooking.airport_id ||
    legacyBooking.airportId ||
    null;
  
  // Normalize coordinates
  if (!normalized.pickup_lat && legacyBooking.pickup_coords) {
    normalized.pickup_lat = legacyBooking.pickup_coords.lat;
    normalized.pickup_lng = legacyBooking.pickup_coords.lng;
  }
  if (!normalized.dropoff_lat && legacyBooking.dropoff_coords) {
    normalized.dropoff_lat = legacyBooking.dropoff_coords.lat;
    normalized.dropoff_lng = legacyBooking.dropoff_coords.lng;
  }
  
  // Normalize dropoff
  normalized.dropoff_location =
    legacyBooking.dropoff_location ||
    legacyBooking.to ||
    legacyBooking.toAddress ||
    legacyBooking.to_address ||
    legacyBooking.destination ||
    '';
  
  return normalized;
};

/**
 * Convert normalized booking to legacy format for older components
 * @param {Object} normalizedBooking - Booking with normalized fields
 * @returns {Object} Booking with legacy fields added
 */
export const toLegacy = (normalizedBooking) => {
  if (!normalizedBooking) return null;
  
  const legacy = { ...normalizedBooking };
  
  // Add legacy pickup fields
  legacy.from = normalizedBooking.pickup_location;
  legacy.fromAirport = normalizedBooking.pickup_location;
  legacy.from_airport = normalizedBooking.pickup_location;
  
  if (normalizedBooking.pickup_airport_code) {
    legacy.airport_id = normalizedBooking.pickup_airport_code;
    legacy.airportId = normalizedBooking.pickup_airport_code;
  }
  
  // Add legacy coordinate format
  if (normalizedBooking.pickup_lat && normalizedBooking.pickup_lng) {
    legacy.pickup_coords = {
      lat: normalizedBooking.pickup_lat,
      lng: normalizedBooking.pickup_lng
    };
    legacy.pickupCoords = legacy.pickup_coords;
  }
  
  // Add legacy dropoff fields
  legacy.to = normalizedBooking.dropoff_location;
  legacy.toAddress = normalizedBooking.dropoff_location;
  legacy.to_address = normalizedBooking.dropoff_location;
  legacy.destination = normalizedBooking.dropoff_location;
  
  if (normalizedBooking.dropoff_lat && normalizedBooking.dropoff_lng) {
    legacy.dropoff_coords = {
      lat: normalizedBooking.dropoff_lat,
      lng: normalizedBooking.dropoff_lng
    };
    legacy.dropoffCoords = legacy.dropoff_coords;
  }
  
  return legacy;
};

/**
 * Create a display-friendly pickup label
 * Handles both legacy and new data structures
 * @param {Object} booking - Booking data (legacy or normalized)
 * @returns {string} Display label
 */
export const getPickupDisplayLabel = (booking) => {
  if (!booking) return 'Unknown';
  
  // Check for new structure first
  if (booking.pickup_type === 'airport' && booking.pickup_airport_code) {
    const airportCode = booking.pickup_airport_code;
    const locationPart = booking.pickup_location?.split(',')[0] || 'Airport';
    return `${airportCode} - ${locationPart}`;
  }
  
  // Fallback to address or legacy fields
  const location = 
    booking.pickup_location ||
    booking.from ||
    booking.fromAirport ||
    booking.from_airport;
  
  if (location) {
    // Return first part of address for cleaner display
    return location.split(',')[0].trim();
  }
  
  return 'Unknown Location';
};

/**
 * Create a display-friendly dropoff label
 * @param {Object} booking - Booking data
 * @returns {string} Display label
 */
export const getDropoffDisplayLabel = (booking) => {
  if (!booking) return 'Unknown';
  
  const location = 
    booking.dropoff_location ||
    booking.to ||
    booking.toAddress ||
    booking.to_address ||
    booking.destination;
  
  if (location) {
    return location.split(',')[0].trim();
  }
  
  return 'Unknown Location';
};

/**
 * Get pickup source label for display
 * @param {Object} booking - Booking data
 * @returns {string} Source label (e.g., "Airport", "Google Places", "Manual")
 */
export const getPickupSourceLabel = (booking) => {
  if (!booking) return 'Unknown';
  
  const type = booking.pickup_type;
  
  if (type === 'airport') {
    return 'Airport';
  }
  if (type === 'custom') {
    return 'Google Places';
  }
  if (type === 'manual') {
    return 'Manual Entry';
  }
  
  // Infer from data if type not set
  if (booking.pickup_airport_code || booking.airport_id) {
    return 'Airport';
  }
  if (booking.pickup_place_id) {
    return 'Google Places';
  }
  
  return 'Manual Entry';
};

/**
 * Get coordinates from booking (handles both legacy and new format)
 * @param {Object} booking - Booking data
 * @param {string} type - 'pickup' or 'dropoff'
 * @returns {Object|null} { lat, lng } or null
 */
export const getCoordinates = (booking, type = 'pickup') => {
  if (!booking) return null;
  
  if (type === 'pickup') {
    if (booking.pickup_lat && booking.pickup_lng) {
      return { lat: booking.pickup_lat, lng: booking.pickup_lng };
    }
    if (booking.pickup_coords) {
      return booking.pickup_coords;
    }
    if (booking.pickupCoords) {
      return booking.pickupCoords;
    }
  } else {
    if (booking.dropoff_lat && booking.dropoff_lng) {
      return { lat: booking.dropoff_lat, lng: booking.dropoff_lng };
    }
    if (booking.dropoff_coords) {
      return booking.dropoff_coords;
    }
    if (booking.dropoffCoords) {
      return booking.dropoffCoords;
    }
  }
  
  return null;
};

/**
 * Generate Google Maps URL for location
 * @param {Object} coords - { lat, lng }
 * @returns {string} Google Maps URL
 */
export const getGoogleMapsUrl = (coords) => {
  if (!coords || !coords.lat || !coords.lng) return null;
  return `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;
};

/**
 * Generate Google Maps directions URL
 * @param {Object} pickupCoords - { lat, lng }
 * @param {Object} dropoffCoords - { lat, lng }
 * @returns {string} Google Maps directions URL
 */
export const getGoogleMapsDirectionsUrl = (pickupCoords, dropoffCoords) => {
  if (!pickupCoords || !dropoffCoords) return null;
  return `https://www.google.com/maps/dir/?api=1&origin=${pickupCoords.lat},${pickupCoords.lng}&destination=${dropoffCoords.lat},${dropoffCoords.lng}`;
};

export default {
  fromLegacy,
  toLegacy,
  getPickupDisplayLabel,
  getDropoffDisplayLabel,
  getPickupSourceLabel,
  getCoordinates,
  getGoogleMapsUrl,
  getGoogleMapsDirectionsUrl
};
